import React, { useCallback, useEffect, useState } from "react";
import { SyncIconWrapper, SyncLabel, SyncLoadingWrapper, SyncWrapper } from "./Sync.styles";

import COLORS from 'shared/config/colors';
import { CircularProgress } from "@material-ui/core";
import PropTypes from "prop-types";
import QuaternaryButton from "shared/components/inputs/button/QuaternaryButton";
import { VARIANTS } from 'shared/components/data-display/text/Text';
import { format } from "date-fns";

/**
 * Sync - Reusable last-sync indicator with optional date, time and refresh icon.
 *
 * @param {boolean}  showIcon          - Show the sync button / loading spinner. Default: true
 * @param {boolean}  showDate          - Show the date portion (e.g. "Feb 26"). Default: false
 * @param {boolean}  showTime          - Show the time portion (HH:MM local). Default: true
 * @param {Function} onRefresh         - Callback fired when the sync button is clicked.
 * @param {boolean}  isLoading         - Replace the sync icon with a loading spinner. Default: false
 * @param {number|null} showAfterMinutes - null → always show the datetime section.
 *                                        number → only show datetime after N minutes have elapsed
 *                                        since the last sync. Default: null
 * @param {number}   updateInterval    - How often (ms) the component re-evaluates elapsed time.
 *                                       Default: 60000 (1 minute)
 */
const Sync = ({
    showIcon = true,
    showDate = false,
    showTime = true,
    onRefresh,
    isLoading = false,
    showAfterMinutes = null,
    updateInterval = 60000,
}) => {
    const [syncDateTime, setSyncDateTime] = useState(() => new Date());
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, updateInterval);
        return () => clearInterval(interval);
    }, [updateInterval]);

    const handleRefresh = useCallback(() => {
        setSyncDateTime(new Date());

        if (onRefresh) {
            onRefresh();
        }

    }, [onRefresh]);

    const minutesSinceSync = (now - syncDateTime) / 60000;

    const shouldShowDateTime =
        (showDate || showTime) &&
        (showAfterMinutes === null || minutesSinceSync >= showAfterMinutes);

    const timeValue = [
        showDate && format(syncDateTime, "MMM d"),
        showTime && format(syncDateTime, "HH:mm"),
    ]
        .filter(Boolean)
        .join(", ");

    return (
        <SyncWrapper>
            {isLoading ? (
                <>
                    <SyncLabel variant={VARIANTS.Descriptive_1_1} color={COLORS.Gray_2}>Syncing...</SyncLabel>
                    {showIcon && (
                        <SyncLoadingWrapper>
                            <CircularProgress style={{ height: "12px", width: "12px" }} />
                        </SyncLoadingWrapper>
                    )}
                </>
            ) : (
                <>
                    {shouldShowDateTime && (
                        <>
                            <SyncLabel variant={VARIANTS.Descriptive_1_1} color={COLORS.Gray_2}>
                                Last Sync
                            </SyncLabel>
                            <SyncLabel variant={VARIANTS.Descriptive_1_2} color={COLORS.Gray_1} >
                                {timeValue}
                            </SyncLabel>
                        </>
                    )}
                    {showIcon && (
                        <SyncIconWrapper>
                            <QuaternaryButton
                                aria-label="Sync"
                                trailingIcon="sync"
                                onClick={handleRefresh}
                            />
                        </SyncIconWrapper>
                    )}
                </>
            )}
        </SyncWrapper>
    );
};

Sync.propTypes = {
    showIcon: PropTypes.bool,
    showDate: PropTypes.bool,
    showTime: PropTypes.bool,
    onRefresh: PropTypes.func,
    isLoading: PropTypes.bool,
    showAfterMinutes: PropTypes.number,
    updateInterval: PropTypes.number,
};

export default Sync;
